<template>
  <div class="columns writing">
    <div class="column">
      <CustomTextAreaField :content=inputs.text :auto-grow="true" :min-height="220" />
      <p class="counter" :class="lengthClass">
        {{ formatNumber(count) }} caractères<span v-if="advice"> · {{ advice }}</span>
        <span v-if="placeholders > 0" class="warning"> · {{ placeholders }} passage{{ placeholders > 1 ? 's' : '' }} [À COMPLÉTER] à remplacer</span>
      </p>
      <div class="status-line">
        <span class="status-label">Le texte est</span>
        <div class="chips" role="group" aria-label="État du texte">
          <button v-for="status in textStatuses" :key="status.value" type="button" class="chip" :class="{selected: inputs.textStatus.value === status.value}"
                  :aria-pressed="inputs.textStatus.value === status.value" :disabled="!hasText" @click="selfInputs.textStatus.value = status.value">{{ status.label }}</button>
        </div>
      </div>
      <CustomTextAreaField :content=inputs.firstComment :auto-grow="true" :min-height="52" />
      <CustomTextField :content=inputs.hashtags />
    </div>

    <div class="column">
      <h3 class="panel-title">Aide à la rédaction</h3>
      <p class="hint">Choisissez une structure, copiez le prompt, collez-le dans ChatGPT, puis ramenez sa réponse ici.</p>
      <div class="chips" role="group" aria-label="Structure du post">
        <button v-for="template in templates" :key="template.id" type="button" class="chip" :class="{selected: effectiveTemplateId === template.id}"
                :aria-pressed="effectiveTemplateId === template.id" :title="template.description" @click="selfInputs.templateId.value = template.id">{{ template.label }}</button>
      </div>
      <p v-if="selectedTemplate" class="hint">{{ selectedTemplate.description }}</p>
      <button type="button" class="panel-button key" :disabled="copying" @click="copy">Copier le prompt</button>

      <label class="field-label" for="pasted-response">Réponse de ChatGPT</label>
      <textarea id="pasted-response" v-model="pasted" class="paste" placeholder="Collez ici la réponse complète, avec ses repères ===POST===, ===PREMIER COMMENTAIRE===…"></textarea>
      <button type="button" class="panel-button" :disabled="!pasted.trim()" @click="split">Répartir dans les champs</button>
      <p class="hint">Le texte, le premier commentaire et l'idée d'image sont rangés pour vous ; les points à vérifier rejoignent la liste du sujet.</p>
    </div>
  </div>
</template>

<script>
import CustomTextField from "@/components/forms/customs/customTextField/CustomTextField.vue";
import CustomTextAreaField from "@/components/forms/customs/customTextAreaField/CustomTextAreaField.vue";
import {NotificationService} from "@vues/v-common";
import {CADENCE_ENUM, countCharacters, formatNumber, splitResponse} from "@/marketing/utils";

const PLACEHOLDER = /\[À COMPLÉTER/gi;

/**
 * Onglet Rédaction : le texte du post, son premier commentaire, ses hashtags et l'aide à la rédaction (prompt à copier, réponse à coller).
 * copyPrompt(templateId) copie le prompt ; applyResponse(parts) range la réponse découpée dans les champs (tous deux portés par la modale).
 */
export default {
  name: 'PublicationWriting',
  props: ['publication', 'topic', 'inputs', 'templates', 'defaultTemplateId', 'copyPrompt', 'applyResponse'],
  components: {CustomTextField, CustomTextAreaField},
  data() {
    return {
      // Les champs appartiennent à la modale : ce composant les modifie par cette référence
      selfInputs: this.inputs,
      pasted: '',
      copying: false,
      textStatuses: [{label: "Un brouillon", value: "DRAFT"}, {label: "Terminé", value: "FINAL"}],
    };
  },
  computed: {
    hasText() {
      return String(this.inputs.text.value ?? '').trim() !== '';
    },
    count() {
      return countCharacters(this.inputs.text.value);
    },
    cadence() {
      return Object.values(CADENCE_ENUM).find(item => item.value === this.topic?.cadence);
    },
    // Longueur conseillée du rythme du sujet
    advice() {
      if (!this.cadence) return '';
      return this.cadence.min ? `${formatNumber(this.cadence.min)} à ${formatNumber(this.cadence.max)} conseillés` : `${formatNumber(this.cadence.max)} au plus`;
    },
    lengthClass() {
      if (!this.cadence || this.count === 0) return '';
      return this.count > this.cadence.max || (this.cadence.min && this.count < this.cadence.min) ? 'out' : 'in';
    },
    placeholders() {
      return (String(this.inputs.text.value ?? '').match(PLACEHOLDER) ?? []).length;
    },
    effectiveTemplateId() {
      return this.inputs.templateId.value || this.defaultTemplateId;
    },
    selectedTemplate() {
      return this.templates.find(template => template.id === this.effectiveTemplateId);
    },
  },
  methods: {
    formatNumber,
    async copy() {
      this.copying = true;
      await this.copyPrompt(this.effectiveTemplateId);
      this.copying = false;
    },
    split() {
      const parts = splitResponse(this.pasted);
      if (!parts) {
        (new NotificationService).addNotification("danger", "Réponse non reconnue : elle doit contenir le repère ===POST===");
        return;
      }
      this.applyResponse(parts);
      this.pasted = '';
    },
  },
}
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';
@require '~/src/marketing/style/modal.styl';

.column
  min-width 0

.counter
  margin 4px 2px 6px
  color CTRL_TEXT_MUTED
  font-size 0.85em
  font-variant-numeric tabular-nums

  &.in
    color #2f7d4a

  &.out
    color #b45309

  .warning
    color #b45309
    font-weight 700

.status-line
  display flex
  align-items center
  flex-wrap wrap
  gap 10px
  margin-bottom 4px

  .status-label
    color CTRL_LABEL
    font-size 0.85em
    font-weight 600

.field-label
  display block
  margin 12px 0 4px 2px
  color CTRL_TEXT_MUTED
  font-size 0.82em
  font-weight 600

.paste
  box-sizing border-box
  width 100%
  height 96px
  margin-bottom 8px
  padding 10px CTRL_PADDING_X
  border 1px solid CTRL_BORDER
  border-radius CTRL_RADIUS
  background CTRL_BG
  color CTRL_TEXT
  font-family inherit
  font-size 13px
  line-height 1.4
  resize none
  outline none
  transition background-color CTRL_EASE, border-color CTRL_EASE, box-shadow CTRL_EASE

  &::placeholder
    color CTRL_PLACEHOLDER

  &:hover
    background CTRL_BG_HOVER
    border-color CTRL_BORDER_HOVER

  &:focus
    background CTRL_BG_FOCUS
    border-color CTRL_FOCUS
    box-shadow 0 0 0 3px CTRL_FOCUS_RING

.panel-button.key
  margin-top 8px
</style>
